import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterDate } from '@/common/hooks/useFilterDate';
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { equipmentConsumptionHistoryColumns } from '@/features/privateCompanies/constants/equipmentConsumptionHistoryColumns.constant'
import { useEquipmentEvents } from '@/features/privateCompanies/hooks/useEquipmentEvents'
import { UserEventData, UserEventTableItem } from '@/features/privateCompanies/types/userEvents.type'
import { EquipmentEventsTransformer } from '@/features/privateCompanies/services/equipments/equipmentEventsTransformer'
import { ArrowBigRightIcon, SearchIcon } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import QueryString from 'qs';
import { CompanyEquipmentData } from '../../types/companyEquipments.type';
import { CompanyData } from '@/features/companies/types/companies.type';
import { useMemo } from 'react';
import Button from '@/common/components/Button';
import useUserPermissions from '@/common/hooks/useUserPermissions';

interface ClientEquipmentConsumptionHistoryProps {
  company?: CompanyData;
  equipment?: CompanyEquipmentData;
}

export const ClientEquipmentConsumptionHistory = ({ company, equipment }: ClientEquipmentConsumptionHistoryProps) => {
  const { id: equipmentId } = useParams()

  const { hasUserPermissionWithProperty, isUserPermissionsPending } = useUserPermissions();

  const { filterParams, setFilterParams } = useFilterParams();
  const { date, formattedDate } = useFilterDate(filterParams);

  const [search, setSearch] = useDebounce(filterParams.consumptionHistorySearch || '');

  const categoryFieldForDisplayName = equipment?.model?.category?.field_for_display;

  const equipmentDisplayName = useMemo(() => {
    return (categoryFieldForDisplayName && equipment?.[categoryFieldForDisplayName as keyof typeof equipment] as string) ||
      equipment?.license_plate ||
      equipment?.official_name ||
      equipment?.serial_number ||
      'Não informado';
  }, [equipment?.id, categoryFieldForDisplayName]);

  const reportUrl = QueryString.stringify({
    generator_klass: 'Reports::Admin::ClientEquipmentConsumptionsReport',
    q: {
      client_company_id_eq: {
        label: company?.general_information?.fantasy_name,
        value: company?.id,
      },
      client_equipment_id_eq: {
        label: equipmentDisplayName,
        value: equipmentId,
      },
      created_at_gteq: date.from?.toISOString(),
      created_at_lteq: date.to?.toISOString(),
    },
  });

  const {
    data: equipmentEvents,
    meta,
    isFetching: equipmentEventsIsFetching,
  } = useEquipmentEvents({
    filters: {
      page: filterParams.page || 1,
      equipmentId,
      search,
      startDate: formattedDate.from,
      endDate: formattedDate.to,
    },
    mapper: Mapper.mapWithTransformer<UserEventData[], UserEventTableItem[]>({
      transformer: EquipmentEventsTransformer.tableTransformer(),
    }),
  });

  const hasPermissionToCreateReport = useMemo(() => {
    return hasUserPermissionWithProperty({
      operationKlass: 'Admin::Reports::Entries::Create',
      propertyKey: 'generator_klass',
      propertyValue: 'Reports::Admin::ClientEquipmentConsumptionsReport',
    });
  }, [isUserPermissionsPending]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <TextField
          LeftIcon={<SearchIcon />}
          placeholder="Pesquisar"
          className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
          value={filterParams.consumptionHistorySearch || ''}
          onChange={(e: any) => {
            setSearch(e.target?.value)
            setFilterParams((prev) => ({
              ...prev,
              consumptionHistorySearch: e.target?.value,
            }))
          }}
        />

        <div className="flex items-center justify-between">
          <DatePickerWithRange
            date={date}
            setDate={(date) => setFilterParams((prev) => ({ ...prev, date }))}
          />

          <Link to={`/reports/create?${reportUrl}`}>
            <Button
              className="py-2 px-4"
              disabled={!hasPermissionToCreateReport}
              tooltipMessage={!hasPermissionToCreateReport ? 'Você não tem permissão' : undefined}
            >
              Exportar tabela

              <ArrowBigRightIcon size={18} />
            </Button>
          </Link>
        </div>
      </div>

      <Table
        className="max-h-[calc(100vh-324px)]"
        columns={equipmentConsumptionHistoryColumns}
        isLoadingData={equipmentEventsIsFetching}
        rows={equipmentEvents}
        rowEmptyValue="--"
        hasPagination
        page={filterParams.page || 1}
        setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
        totalPages={meta.total_pages}
        totalCount={meta.total}
      />
    </div>
  )
}
